/**
 * useAuthorizationForm Hook
 * Manages authorization form state, validation, and submission
 */

import { useState, useEffect, useCallback } from 'react';
import {
  AuthorizationsService,
  Authorization,
  AuthorizationRequest,
  AuthorizationDocument,
  AuthorizationDiscipline,
  AuthorizationStatus,
  QuantityType,
  HCPCS_BY_DISCIPLINE
} from '../../../../services/patientmr/authorizations.service';
import { validateUploadFile } from '../../../../services/uploads/presignedUpload';
import { AuthorizationFormErrors, POCCertification } from '../types';

export interface UseAuthorizationFormOptions {
  patientId?: string;
  pocCertifications: POCCertification[];
  onSuccess?: (authorization: Authorization) => void;
  onError?: (error: string) => void;
}

export interface UseAuthorizationFormResult {
  // Form state
  formData: AuthorizationRequest;
  isEditing: boolean;
  isViewing: boolean;
  saving: boolean;
  errors: AuthorizationFormErrors;

  // File upload: staged files go up after the record is saved (pre-signed S3 POST, Phase 8).
  // No per-file progress here; `uploading` drives an indeterminate spinner.
  uploadedFiles: File[];
  uploading: boolean;
  handleFileSelect: (files: File[]) => void;
  removeFile: (file: File) => void;

  // Custom time per unit
  isCustomTimePerUnit: boolean;
  customTimePerUnitValue: string;
  setIsCustomTimePerUnit: (value: boolean) => void;
  setCustomTimePerUnitValue: (value: string) => void;

  // Form actions
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  openAddForm: () => void;
  openEditForm: (auth: Authorization) => void;
  openViewForm: (auth: Authorization) => void;
  cancelForm: () => void;
  handleDeleteDocument: (doc: AuthorizationDocument, authId: string | number) => Promise<void>;
}

const DEFAULT_FORM_DATA: AuthorizationRequest = {
  discipline: AuthorizationDiscipline.RN,
  startDate: '',
  expirationDate: '',
  authNumber: '',
  authorizedVisits: 0,
  quantityType: QuantityType.VISITS,
  timePerUnit: HCPCS_BY_DISCIPLINE['RN'].timePerUnit,
  hcpcsCode: HCPCS_BY_DISCIPLINE['RN'].code,
  notes: '',
  status: AuthorizationStatus.ACTIVE,
  pocCertificationId: '',
  unitsUsed: 0,
  lastClaimDate: '',
  payorName: '',
  payorPhone: '',
  caseManagerName: '',
  caseManagerPhone: '',
  denialReason: '',
  appealDate: '',
  appealOutcome: ''
};

export function useAuthorizationForm({
  patientId,
  pocCertifications,
  onSuccess,
  onError
}: UseAuthorizationFormOptions): UseAuthorizationFormResult {
  const [formData, setFormData] = useState<AuthorizationRequest>(DEFAULT_FORM_DATA);
  const [editingAuth, setEditingAuth] = useState<Authorization | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<AuthorizationFormErrors>({});

  // Custom time per unit state
  const [isCustomTimePerUnit, setIsCustomTimePerUnit] = useState(false);
  const [customTimePerUnitValue, setCustomTimePerUnitValue] = useState('');

  // Staged files; uploaded after the record is saved (no simulated progress)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Handle file selection (same client checks as the pre-signed upload: PDF/JPG/PNG <= 10 MB)
  const handleFileSelect = (files: File[]) => {
    const problems = files.map(validateUploadFile).filter((m): m is string => m !== null);
    const validFiles = files.filter(file => validateUploadFile(file) === null);

    if (problems.length > 0 && onError) {
      onError(problems.join(' '));
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  // Remove file
  const removeFile = (file: File) => {
    setUploadedFiles(prev => prev.filter(f => f !== file));
  };

  // Reset file upload
  const resetFileUpload = useCallback(() => {
    setUploadedFiles([]);
    setUploading(false);
  }, []);

  // Auto-populate HCPCS code and time per unit when discipline changes
  useEffect(() => {
    const currentDiscipline = formData.discipline;
    if (!currentDiscipline) return;

    const billingDefaults = HCPCS_BY_DISCIPLINE[currentDiscipline];
    if (!billingDefaults) return;

    setFormData(prev => ({
      ...prev,
      hcpcsCode: billingDefaults.code,
      timePerUnit: billingDefaults.timePerUnit
    }));
  }, [formData.discipline]);

  // Handle input changes
  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: name === 'authorizedVisits' ? parseInt(value) || 0 : value
    }));

    // Clear field-specific error
    if (errors[name as keyof AuthorizationFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  // Validate form
  const validateForm = useCallback((data: AuthorizationRequest): AuthorizationFormErrors => {
    const formErrors: AuthorizationFormErrors = {};
    const generalErrors: string[] = [];

    if (!data.discipline) {
      formErrors.discipline = 'Service type is required';
      generalErrors.push('Service type is required');
    }

    if (!data.authNumber?.trim()) {
      formErrors.authNumber = 'Authorization number is required';
      generalErrors.push('Authorization number is required');
    }

    if (!data.startDate) {
      formErrors.startDate = 'Start date is required';
      generalErrors.push('Start date is required');
    }

    if (!data.expirationDate) {
      formErrors.expirationDate = 'End date is required';
      generalErrors.push('End date is required');
    } else if (data.startDate && new Date(data.expirationDate) <= new Date(data.startDate)) {
      formErrors.expirationDate = 'End date must be after start date';
      generalErrors.push('End date must be after start date');
    }

    // Link to POC Certification validation
    if (data.pocCertificationId) {
      const linkedPOC = pocCertifications.find(p => (p.pocCertificationId || p.id) === data.pocCertificationId);
      if (linkedPOC && linkedPOC.status !== 'signed') {
        generalErrors.push('Linked POC certification must be signed before authorization can be active');
      }
    }

    // Units used cannot exceed authorized
    if (data.unitsUsed && data.authorizedVisits && data.unitsUsed > data.authorizedVisits) {
      generalErrors.push('Units used cannot exceed authorized units');
    }

    // Appeal logic validation
    if (data.appealOutcome && !data.appealDate) {
      generalErrors.push('Appeal date required if appeal outcome is specified');
    }

    if (generalErrors.length > 0) {
      formErrors.general = generalErrors;
    }

    return formErrors;
  }, [pocCertifications]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!patientId) {
      if (onError) onError('Patient ID is required to save authorization. Please select a patient.');
      return;
    }

    const validationErrors = validateForm(formData);
    if (validationErrors.general && validationErrors.general.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);

      // Documents never ride on the save payload (R-128); they upload after the save below.
      const { documents: _documents, ...submissionData } = formData;

      let result: Authorization;
      if (isEditing && editingAuth) {
        result = await AuthorizationsService.updateAuthorization(
          patientId,
          editingAuth.id,
          submissionData
        );
      } else {
        result = await AuthorizationsService.createAuthorization(
          patientId,
          submissionData
        );
      }

      // Upload staged files to the saved record (pre-signed POST + confirm)
      const recordId = result.authorizationId || String(result.id);
      if (uploadedFiles.length > 0 && recordId) {
        setUploading(true);
        const failed: string[] = [];
        for (const file of uploadedFiles) {
          try {
            await AuthorizationsService.uploadDocument(patientId, recordId, file);
          } catch (uploadErr) {
            failed.push(uploadErr instanceof Error && uploadErr.message ? uploadErr.message : `"${file.name}" failed to upload.`);
          }
        }
        setUploading(false);
        if (failed.length > 0 && onError) {
          onError(`Authorization saved, but some documents did not upload: ${failed.join(' ')}`);
        }
      }

      if (onSuccess) onSuccess(result);
      cancelForm();
    } catch (err: any) {
      console.error('Error saving authorization:', err);
      if (onError) onError('Failed to save authorization. Please try again.');
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cancelForm is declared later (stable via useCallback); listing it here would hit the temporal dead zone
  }, [patientId, formData, uploadedFiles, isEditing, editingAuth, validateForm, onSuccess, onError]);

  // Open add form
  const openAddForm = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
    setEditingAuth(null);
    setIsEditing(false);
    setIsViewing(false);
    setErrors({});
    setIsCustomTimePerUnit(false);
    setCustomTimePerUnitValue('');
    resetFileUpload();
  }, [resetFileUpload]);

  // Open edit form
  const openEditForm = useCallback((auth: Authorization) => {
    setFormData({
      discipline: auth.discipline,
      startDate: auth.startDate,
      expirationDate: auth.expirationDate,
      authNumber: auth.authNumber,
      authorizedVisits: auth.authorizedVisits || auth.visits || 0,
      quantityType: auth.quantityType || QuantityType.VISITS,
      timePerUnit: auth.timePerUnit,
      hcpcsCode: auth.hcpcsCode,
      notes: auth.notes || '',
      status: auth.status as AuthorizationStatus,
      pocCertificationId: auth.pocCertificationId || '',
      unitsUsed: auth.unitsUsed || 0,
      lastClaimDate: auth.lastClaimDate || '',
      payorName: auth.payorName || '',
      payorPhone: auth.payorPhone || '',
      caseManagerName: auth.caseManagerName || '',
      caseManagerPhone: auth.caseManagerPhone || '',
      denialReason: auth.denialReason || '',
      appealDate: auth.appealDate || '',
      appealOutcome: auth.appealOutcome || ''
    });
    setEditingAuth(auth);
    setIsEditing(true);
    setIsViewing(false);
    setErrors({});

    // Check custom time per unit
    const predefinedTimePerUnit = ['15 minutes', '30 minutes', '45 minutes'];
    if (auth.timePerUnit && !predefinedTimePerUnit.includes(auth.timePerUnit)) {
      setIsCustomTimePerUnit(true);
      setCustomTimePerUnitValue(auth.timePerUnit);
    } else {
      setIsCustomTimePerUnit(false);
      setCustomTimePerUnitValue('');
    }

    resetFileUpload();
  }, [resetFileUpload]);

  // Open view form
  const openViewForm = useCallback((auth: Authorization) => {
    setFormData({
      discipline: auth.discipline,
      startDate: auth.startDate,
      expirationDate: auth.expirationDate,
      authNumber: auth.authNumber,
      authorizedVisits: auth.authorizedVisits || auth.visits || 0,
      quantityType: auth.quantityType || QuantityType.VISITS,
      timePerUnit: auth.timePerUnit,
      hcpcsCode: auth.hcpcsCode,
      notes: auth.notes || '',
      status: auth.status as AuthorizationStatus,
      pocCertificationId: auth.pocCertificationId || '',
      unitsUsed: auth.unitsUsed || 0,
      lastClaimDate: auth.lastClaimDate || '',
      payorName: auth.payorName || '',
      payorPhone: auth.payorPhone || '',
      caseManagerName: auth.caseManagerName || '',
      caseManagerPhone: auth.caseManagerPhone || '',
      denialReason: auth.denialReason || '',
      appealDate: auth.appealDate || '',
      appealOutcome: auth.appealOutcome || ''
    });
    setEditingAuth(auth);
    setIsViewing(true);
    setIsEditing(false);
    setErrors({});
  }, []);

  // Cancel form
  const cancelForm = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
    setEditingAuth(null);
    setIsEditing(false);
    setIsViewing(false);
    setErrors({});
    setIsCustomTimePerUnit(false);
    setCustomTimePerUnitValue('');
    resetFileUpload();
  }, [resetFileUpload]);

  // Handle document deletion
  const handleDeleteDocument = useCallback(async (doc: AuthorizationDocument, authId: string | number) => {
    if (!patientId) return;

    try {
      const signedUrlResponse = await AuthorizationsService.getDocumentSignedUrl(patientId, authId, doc.documentId);
      const signedUrl = signedUrlResponse?.signedUrl;

      if (signedUrl) {
        window.open(signedUrl, '_blank');
      } else {
        if (onError) onError('Failed to get download URL');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      if (onError) onError('Failed to download document');
    }
  }, [patientId, onError]);

  return {
    formData,
    isEditing,
    isViewing,
    saving,
    errors,
    uploadedFiles,
    uploading,
    handleFileSelect,
    removeFile,
    isCustomTimePerUnit,
    customTimePerUnitValue,
    setIsCustomTimePerUnit,
    setCustomTimePerUnitValue,
    handleInputChange,
    handleSubmit,
    openAddForm,
    openEditForm,
    openViewForm,
    cancelForm,
    handleDeleteDocument
  };
}
